<script setup lang="ts">
import ReportTemplateMenu from '@/components/createdUi/ReportTemplateMenu.vue'

import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useDisplay } from 'vuetify/framework'
import { VMaskInput } from 'vuetify/labs/VMaskInput'
import { storeToRefs } from 'pinia'

import CopyEntityLink from '@/components/createdUi/CopyEntityLink.vue'
import EntityPaneEar from '@/components/createdUi/EntityPaneEar.vue'
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import CustomDivider from '@/components/createdUi/CustomDivider.vue'
import EmployeeSelectionModal from '@/components/createdUi/EmployeeSelectionModal.vue'
import NewTreeComponent from '@/components/createdUi/NewTreeComponent.vue'
import SearchableSelectList, { type ISearchableSelectItem } from '@/components/createdUi/SearchableSelectList.vue'
import { searchDepartments } from '@/components/createdUi/treeSearch'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import { useDialogDismissGuard } from '@/composables/useDialogDismissGuard'
import { useApiStore } from '@/modules/apiStore/stores/apiStore'
import type { IEmployeeTreeItem } from '@/modules/apiStore/types/employees/IEmployeeTree'
import type { ITreeNode } from '@/modules/apiStore/types/ITreeNode'
import { useFormEmbeddedLayout } from '@/composables/ui/useFormEmbeddedLayout'
import { useResizablePane } from '@/composables/ui/useResizablePane'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import FileList from '@/modules/files/components/FileList.vue'
import type { IExistingFile } from '@/modules/files/types/IExistingFile'
import { usePermissionsStore } from '@/modules/permissions/stores/permissionsStore'
import TaskCard from '@/modules/tasks/components/TaskCard.vue'
import TaskUnassignedCard from '@/modules/tasks/components/TaskUnassignedCard.vue'
import { useUserStore } from '@/modules/user/stores/userStore'
import { ERouteNames } from '@/router/ERouteNames'
import { formatDateTime, getPersonDisplayName } from '@/utils/formatters'
import { useCitizenInquiriesStore } from '../stores/citizenInquiriesStore'
import OnboardingLaunchButton from '@/modules/onboarding/components/OnboardingLaunchButton.vue'
import { useOnboardingContext } from '@/modules/onboarding/composables/useOnboardingContext'
import { useOnboardingStore } from '@/modules/onboarding/stores/onboardingStore'
import { APPEALS_ANCHORS, APPEALS_CONTEXTS } from '@/modules/onboarding/scenarios/appeals/anchors'
import { APPEALS_FORM_DEMO_INDEX } from '@/modules/onboarding/scenarios/appeals/adapter'
import {
  APPEAL_STATUS_LABELS,
  APPEAL_TARGET_TYPE_LABELS,
  APPEAL_TOPIC_LABELS,
  type AppealTargetType,
  type AppealTopic,
  type IAppealDetail,
} from '../types/IAppeal'
import type { ICreateAppealPayload, IUpdateAppealPayload } from '../types/IAppealPayloads'

const props = withDefaults(defineProps<{
  appealId?: string | number
  isGlobal?: boolean
}>(), {
  isGlobal: false,
})

const route = useRoute()
const { xs, smAndDown, mdAndUp } = useDisplay()
const feedbackStore = useFeedbackStore()
const store = useCitizenInquiriesStore()
const permissionsStore = usePermissionsStore()
const apiStore = useApiStore()
const userStore = useUserStore()

const { currentAppeal } = storeToRefs(store)

const visible = ref(true)
const showUnsavedChangesDialog = ref(false)
const isTasksPaneVisible = ref(true)

const coordinatorModalRef = ref<InstanceType<typeof EmployeeSelectionModal> | null>(null)
const targetEmployeeModalRef = ref<InstanceType<typeof EmployeeSelectionModal> | null>(null)

const entityId = computed(() => {
  if (props.isGlobal) return props.appealId != null ? String(props.appealId) : null
  return route.params.id ? String(route.params.id) : null
})

const isEditMode = computed(() => Boolean(entityId.value))
const isCreateMode = computed(() => !isEditMode.value)
const pinCreateChrome = computed(() => isCreateMode.value)
const formEmbeddedLayout = useFormEmbeddedLayout(isEditMode)

// Без ушек (мобилка) по умолчанию показываем форму; ниже watch globalFormTab === 2 может открыть задачи
if (!formEmbeddedLayout.isEmbedded.value)
  isTasksPaneVisible.value = false

/* Права на редактирование и координацию */
const canPickAnyCoordinator = computed(() => Boolean(
  permissionsStore.isSuperuser
  || permissionsStore.modules.appeals?.coordinator_edit
  || currentAppeal.value?.editable_fields?.includes('coordinator_entry'),
))

const canAssignMyself = computed(() => Boolean(
  isEditMode.value
  && !currentAppeal.value?.coordinator_entry?.responsible_employee
  && Boolean(userStore.user?.id)
  && (
    permissionsStore.isSuperuser
    || permissionsStore.modules.appeals?.coordinator_edit
    || permissionsStore.modules.appeals?.reply
  ),
))

const hasTasksPane = computed(() => Boolean(
  isEditMode.value
  && (currentAppeal.value?.coordinator_entry || canPickAnyCoordinator.value || canAssignMyself.value),
))

const isRightPaneVisible = computed(() => Boolean(
  isEditMode.value && isTasksPaneVisible.value && hasTasksPane.value,
))

const overlayContentClass = computed(() => {
  if (formEmbeddedLayout.contentClass.value) {
    return [
      formEmbeddedLayout.contentClass.value,
      {
        'entity-form-embedded--no-chat': !isRightPaneVisible.value,
      },
    ]
  }
  if (pinCreateChrome.value)
    return 'entity-form-create-dialog'
  return undefined
})

/* Правая панель задач: управление шириной через ресайзер */
const chatPaneResizer = useResizablePane({
  initial: Math.floor(typeof window !== 'undefined' ? window.innerWidth * 0.42 : 540),
  min: 420,
  max: () => Math.floor(typeof window !== 'undefined' ? window.innerWidth * 0.55 : 700),
  storageKey: 'appeal-tasks-pane-width',
})

const toggleTasksPane = () => {
  isTasksPaneVisible.value = !isTasksPaneVisible.value
}

watch(() => feedbackStore.globalFormTab, tab => {
  if (tab === 2) {
    isTasksPaneVisible.value = true
    feedbackStore.globalFormTab = null
  }
}, { immediate: true })

/* Проверка, является ли текущий пользователь назначенным координатором обращения */
const isCurrentUserCoordinator = computed(() => {
  if (permissionsStore.isSuperuser) return true
  const coord = currentAppeal.value?.coordinator || currentAppeal.value?.coordinator_entry?.responsible_employee
  const userId = userStore.user?.id
  if (!coord || !userId) return false
  return String(coord.id) === String(userId)
})

// Бэк (IsAppealCoordinator) пускает к ответу назначенного координатора без права appeals.reply
const canReplyAppeal = computed(() => Boolean(isEditMode.value && isCurrentUserCoordinator.value))

/* Доступность редактирования полей обращения: при создании — всё, при редактировании — только editable_fields с бэка (как в TicketForm.vue) */
const canEditField = (fieldName: string): boolean => {
  if (isCreateMode.value) return true
  return Boolean(currentAppeal.value?.editable_fields?.includes(fieldName))
}

const hasEditableFields = computed(() => Boolean(currentAppeal.value?.editable_fields?.length))

/* Состояние выпадающих меню (SearchableSelectList) */
const showChannelMenu = ref(false)
const showTopicMenu = ref(false)
const showRatingMenu = ref(false)
const showTargetTypeMenu = ref(false)
const selectedTargetDepartment = ref<{ id: string } | null>(null)

/* Состояние формы обращения (создание / редактирование) */
const form = reactive({
  channel: null as number | null,
  topic: 'complaint' as AppealTopic | string,
  name: '',
  email: '',
  phone: '',
  target_type: null as AppealTargetType | string | null,
  target_name: '',
  rating: null as number | null,
  text: '',
})

const validationErrors = reactive({
  channel: false,
  topic: false,
  name: false,
  rating: false,
  text: false,
})

const initialFormSnapshot = ref('')

/* Списки для SearchableSelectList */
const channelSelectItems = computed<ISearchableSelectItem[]>(() => {
  const list = [...store.createChannels]
  const currentCh = currentAppeal.value?.channel
  if (currentCh && !list.some(c => c.id === currentCh.id) && (!currentCh.is_system || isEditMode.value)) {
    list.unshift(currentCh)
  }
  return list.map(c => ({
    id: c.id,
    label: c.name,
  }))
})

const selectedChannelLabel = computed(() => {
  if (form.channel == null) return currentAppeal.value?.channel?.name || ''
  const found = channelSelectItems.value.find(c => Number(c.id) === Number(form.channel))
  return found?.label || currentAppeal.value?.channel?.name || ''
})

const topicSelectItems: ISearchableSelectItem[] = [
  { id: 'complaint', label: 'Жалоба' },
  { id: 'review', label: 'Отзыв' },
  { id: 'wish', label: 'Пожелание' },
]

const selectedTopicLabel = computed(() => {
  return APPEAL_TOPIC_LABELS[form.topic] || form.topic || ''
})

const ratingSelectItems: ISearchableSelectItem[] = [
  { id: 5, label: '5 — Отлично' },
  { id: 4, label: '4 — Хорошо' },
  { id: 3, label: '3 — Удовлетворительно' },
  { id: 2, label: '2 — Плохо' },
  { id: 1, label: '1 — Очень плохо' },
]

const selectedRatingLabel = computed(() => {
  if (!form.rating) return ''
  const found = ratingSelectItems.find(r => Number(r.id) === Number(form.rating))
  return found?.label || `${form.rating} / 5`
})

const targetTypeSelectItems: ISearchableSelectItem[] = [
  { id: 'department', label: 'Отделение' },
  { id: 'employee', label: 'Сотрудник' },
]

const selectedTargetTypeLabel = computed(() => {
  if (!form.target_type) return ''
  return APPEAL_TARGET_TYPE_LABELS[form.target_type] || form.target_type
})

function onSelectChannel(item: ISearchableSelectItem) {
  form.channel = Number(item.id)
  validationErrors.channel = false
  showChannelMenu.value = false
}

function onSelectTopic(item: ISearchableSelectItem) {
  form.topic = String(item.id)
  validationErrors.topic = false
  showTopicMenu.value = false
  if (form.topic !== 'review') {
    form.rating = null
    validationErrors.rating = false
  } else if (form.rating == null) {
    form.rating = 5
  }
}

function onSelectRating(item: ISearchableSelectItem) {
  form.rating = Number(item.id)
  validationErrors.rating = false
  showRatingMenu.value = false
}

function onSelectTargetType(item: ISearchableSelectItem) {
  const newType = String(item.id) as AppealTargetType
  if (form.target_type !== newType) {
    form.target_type = newType
    form.target_name = ''
    selectedTargetDepartment.value = null
  }
  showTargetTypeMenu.value = false
}

function onClearTargetType() {
  form.target_type = null
  form.target_name = ''
  selectedTargetDepartment.value = null
}

/* Поиск отделения в дереве для target_name */
function findTreeNodeById(nodes: ITreeNode[], id: string): ITreeNode | null {
  for (const node of nodes) {
    if (node.key === id || node.data?.selfId === id) return node
    if (node.children?.length) {
      const found = findTreeNodeById(node.children, id)
      if (found) return found
    }
  }
  return null
}

function findTreeNodeByLabel(nodes: ITreeNode[], label: string): ITreeNode | null {
  const normalized = label.trim().toLowerCase()
  if (!normalized) return null
  for (const node of nodes) {
    if (node.label?.trim().toLowerCase() === normalized) return node
    if (node.children?.length) {
      const found = findTreeNodeByLabel(node.children, label)
      if (found) return found
    }
  }
  return null
}

function onTargetDepartmentSelect(val: { id: string } | null) {
  selectedTargetDepartment.value = val
  if (!val?.id) {
    form.target_name = ''
    return
  }
  const node = findTreeNodeById(apiStore.departmentsTree, val.id)
  form.target_name = node?.label || ''
}

function openTargetEmployeeModal() {
  if (!canEditField('target_name')) return
  if (!apiStore.employees?.length) {
    apiStore.fetchAllEmployees()
  }
  targetEmployeeModalRef.value?.open()
}

function onTargetEmployeeSelected(employee: IEmployeeTreeItem | null) {
  if (!employee) return
  form.target_name = employee.full_name || ''
}

/* Форма ответа по электронной почте */
const replySubject = ref('')
const replyBody = ref('')
const replyEmail = ref('')

const getApplicantName = (appeal: IAppealDetail | null) => {
  if (!appeal) return ''
  return appeal.name || appeal.full_name || appeal.patient_name || appeal.author || ''
}

const syncFormFromAppeal = (appeal: IAppealDetail | null) => {
  validationErrors.channel = false
  validationErrors.topic = false
  validationErrors.name = false
  validationErrors.rating = false
  validationErrors.text = false

  if (!appeal) {
    form.channel = store.createChannels[0]?.id ?? null
    form.topic = 'complaint'
    form.name = ''
    form.email = ''
    form.phone = ''
    form.target_type = null
    form.target_name = ''
    form.rating = null
    form.text = ''
    selectedTargetDepartment.value = null
    initialFormSnapshot.value = JSON.stringify(form)
    return
  }

  form.channel = appeal.channel?.id ?? null
  form.topic = appeal.topic || 'complaint'
  form.name = getApplicantName(appeal)
  form.email = appeal.email || ''
  form.phone = appeal.phone || ''
  form.target_type = appeal.target_type || null
  form.target_name = appeal.target_name || ''
  form.rating = appeal.rating ?? null
  form.text = appeal.text || ''

  if (form.target_type === 'department' && form.target_name && apiStore.departmentsTree?.length) {
    const matchedNode = findTreeNodeByLabel(apiStore.departmentsTree, form.target_name)
    selectedTargetDepartment.value = matchedNode ? { id: String(matchedNode.data?.selfId || matchedNode.key) } : null
  } else {
    selectedTargetDepartment.value = null
  }

  initialFormSnapshot.value = JSON.stringify(form)
}

const isFormChanged = computed(() => {
  if (!initialFormSnapshot.value) return false
  return JSON.stringify(form) !== initialFormSnapshot.value
})

const isReplyDirty = computed(() => {
  return replySubject.value.trim().length > 0 || replyBody.value.trim().length > 0 || replyEmail.value.trim().length > 0
})

const isDirty = computed(() => {
  if (isCreateMode.value) return isFormChanged.value
  return isFormChanged.value || (isReplyDirty.value && currentAppeal.value?.status !== 'answered')
})

const existingFiles = computed<IExistingFile[]>(() => {
  if (!currentAppeal.value?.files) return []
  return currentAppeal.value.files.map((f, index) => ({
    id: (f.id as unknown as number) || index + 1,
    file: f.url,
    filename: f.original_name,
    size: f.size,
  }))
})

const initReplyForm = () => {
  replySubject.value = ''
  replyBody.value = ''
  replyEmail.value = ''
}

/*
 * Демо-режим онбординга. Форма открыта на учебном обращении, если его id — из демо-данных онбординга
 * (переход из реестра) или онбординг запущен из формы реального обращения (demoOverrideId).
 * Id учебного обращения фиктивный: в демо-режиме форма ничего не запрашивает по нему.
 */
const onboardingStore = useOnboardingStore()
const demoOverrideId = ref<string | null>(null)
const demoItem = computed(() => onboardingStore.findDemoItem('appeal', demoOverrideId.value ?? entityId.value))
const isDemo = computed(() => Boolean(demoItem.value))

function applyDemoAppeal() {
  if (!demoItem.value)
    return

  store.currentAppeal = JSON.parse(JSON.stringify(demoItem.value.detail))
  syncFormFromAppeal(currentAppeal.value)
  initReplyForm()
}

const loadData = async (id: string) => {
  if (isDemo.value) {
    applyDemoAppeal()
    return
  }
  try {
    await store.fetchAppealById(id)
    // Справочник каналов закрыт без appeals.view (участник задачи получит 403) — грузим только если канал можно менять
    if (canEditField('channel'))
      await store.fetchChannels(true)
    syncFormFromAppeal(currentAppeal.value)
    initReplyForm()
  } catch (e) {
    console.error('Ошибка загрузки обращения:', e)
  }
}

onMounted(async () => {
  await Promise.all([
    !apiStore.departments?.length && apiStore.fetchAllDepartments(),
    !apiStore.employees?.length && apiStore.fetchAllEmployees(),
  ])

  if (entityId.value) {
    await loadData(entityId.value)
  } else {
    store.currentAppeal = null
    await store.fetchChannels(true)
    syncFormFromAppeal(null)
  }
})

watch(entityId, async newId => {
  if (newId) {
    await loadData(newId)
  } else {
    store.currentAppeal = null
    await store.fetchChannels(true)
    syncFormFromAppeal(null)
  }
})

/* Координатор и задачи */
const coordinatorTask = computed(() => {
  if (!currentAppeal.value?.coordinator_entry) return null
  const subtasks = currentAppeal.value.subtasks || currentAppeal.value.coordinator_entry.subtasks || []
  return {
    ...currentAppeal.value.coordinator_entry,
    subtasks,
    is_coordinator_entry: true,
    source_type: 'appeal' as const,
    url_props: {
      appealId: currentAppeal.value.id || entityId.value || undefined,
    },
  } as any
})

const handleCoordinatorTaskUpdated = (updatedTask: any) => {
  if (currentAppeal.value?.coordinator_entry) {
    currentAppeal.value.coordinator_entry = {
      ...currentAppeal.value.coordinator_entry,
      ...updatedTask,
    }
    if (updatedTask?.responsible_employee) {
      currentAppeal.value.coordinator = updatedTask.responsible_employee
    }
  }
}

const openCoordinatorModal = () => {
  if (!canPickAnyCoordinator.value) return
  if (!apiStore.employees?.length) {
    apiStore.fetchAllEmployees()
  }
  coordinatorModalRef.value?.open()
}

const onCoordinatorSelected = async (employee: IEmployeeTreeItem | null) => {
  const id = currentAppeal.value?.local_id || entityId.value
  if (!employee?.id || !id) return
  if (String(currentAppeal.value?.coordinator_entry?.responsible_employee?.id) === String(employee.id)) return

  try {
    await store.changeAppealCoordinator({
      appeal_id: id,
      coordinator_id: employee.id,
    })
    if (entityId.value) {
      await store.fetchAppealById(entityId.value)
    }
  } catch (e) {
    console.error('Ошибка назначения координатора:', e)
  }
}

const assignMyselfAsCoordinator = async () => {
  const currentUserId = userStore.user?.id
  const id = currentAppeal.value?.local_id || entityId.value
  if (!currentUserId || !id) return

  try {
    await store.changeAppealCoordinator({
      appeal_id: id,
      coordinator_id: currentUserId,
    })
    if (entityId.value) {
      await store.fetchAppealById(entityId.value)
    }
  } catch (e) {
    console.error('Ошибка назначения себя координатором:', e)
  }
}

/* Валидация и сохранение обращения */
// Обязательность проверяем только у редактируемых полей: закрытые (не в editable_fields) бэк при PATCH не требует
const validateForm = () => {
  validationErrors.channel = Boolean(canEditField('channel') && !form.channel)
  validationErrors.topic = canEditField('topic') && !form.topic
  validationErrors.name = canEditField('name') && !form.name.trim()
  validationErrors.text = canEditField('text') && !form.text.trim()
  validationErrors.rating = canEditField('rating') && form.topic === 'review' && (!form.rating || form.rating < 1 || form.rating > 5)

  return !Object.values(validationErrors).some(Boolean)
}

const onSaveAppeal = async () => {
  if (!validateForm()) {
    feedbackStore.showToast({
      type: 'error',
      message: 'Заполните все обязательные поля',
      time: 4000,
    })
    return
  }

  const payload: IUpdateAppealPayload = {
    topic: form.topic,
    name: form.name.trim(),
    email: form.email.trim() || '',
    phone: form.phone.trim() || null,
    target_type: form.target_type || null,
    target_name: form.target_name.trim() || null,
    text: form.text.trim(),
    rating: form.topic === 'review' ? form.rating : null,
  }

  if (canEditField('channel') && form.channel) {
    payload.channel = form.channel
  }

  try {
    if (isCreateMode.value) {
      await store.createAppeal(payload as ICreateAppealPayload)
      initialFormSnapshot.value = JSON.stringify(form)
      visible.value = false
      if (props.isGlobal) {
        feedbackStore.globalCitizenInquiryId = null
      } else {
        await feedbackStore.goToParentRoute()
      }
    } else if (entityId.value) {
      const targetId = currentAppeal.value?.local_id || entityId.value
      // PATCH только по изменённым полям: нетронутые поля (ФИО, пустой email из ЛК) бэк не должен перевалидировать
      const initial = JSON.parse(initialFormSnapshot.value) as typeof form
      const changedPayload = Object.fromEntries(
        Object.entries(payload).filter(([key]) => form[key as keyof typeof form] !== initial[key as keyof typeof form]),
      ) as IUpdateAppealPayload
      await store.updateAppeal(targetId, changedPayload)
      syncFormFromAppeal(currentAppeal.value)
    }
  } catch (e) {
    console.error('Ошибка сохранения обращения:', e)
  }
}

/* Отправка официального ответа по электронной почте */
const effectiveAppealEmail = computed(() => {
  return currentAppeal.value?.email?.trim() || form.email?.trim() || replyEmail.value.trim() || ''
})

/**
 * Отправляет ответ на обращение гражданина: доступно только назначенному координатору (или суперпользователю).
 * Если email заявителя не указан, отправляет адрес, введённый координатором.
 */
const onSendReply = async () => {
  if (!entityId.value) return

  if (!canReplyAppeal.value) {
    feedbackStore.showToast({
      type: 'error',
      message: 'Ответ по электронной почте доступен только назначенному координатору обращения',
      time: 4000,
    })
    return
  }

  const targetEmail = currentAppeal.value?.email?.trim() || replyEmail.value.trim() || form.email.trim()
  if (!targetEmail) {
    feedbackStore.showToast({
      type: 'error',
      message: 'Укажите Email заявителя для отправки ответа',
      time: 4000,
    })
    return
  }

  const isValid = replySubject.value.trim() && replyBody.value.trim()
  if (!isValid) {
    feedbackStore.showToast({
      type: 'error',
      message: 'Пожалуйста, заполните тему и текст ответа',
      time: 4000,
    })
    return
  }

  try {
    await store.replyAppeal(entityId.value, {
      subject: replySubject.value.trim(),
      body: replyBody.value.trim(),
      email: !currentAppeal.value?.email?.trim() ? targetEmail : undefined,
    })
    replyBody.value = ''
    replySubject.value = ''
    replyEmail.value = ''
    syncFormFromAppeal(currentAppeal.value)
  } catch (e) {
    console.error('Ошибка отправки ответа:', e)
  }
}

const handleClose = () => {
  if (isDirty.value) {
    showUnsavedChangesDialog.value = true
    return
  }

  visible.value = false
  feedbackStore.globalFormTab = null
  if (props.isGlobal) {
    feedbackStore.globalCitizenInquiryId = null
  } else {
    feedbackStore.goToParentRoute()
  }
}

const handleCloseConfirm = async (confirmed: boolean) => {
  showUnsavedChangesDialog.value = false
  if (confirmed) {
    visible.value = false
    feedbackStore.globalFormTab = null
    if (props.isGlobal) {
      feedbackStore.globalCitizenInquiryId = null
    } else {
      await feedbackStore.goToParentRoute()
    }
  }
}

const onDialogDismissAttempt = useDialogDismissGuard(handleClose)

const getRepliedByLabel = (appeal: IAppealDetail | null) => {
  if (!appeal?.replied_by) return ''
  return getPersonDisplayName(appeal.replied_by as any)
}

const getCreatedByLabel = (appeal: IAppealDetail | null) => {
  if (!appeal?.created_by) return ''
  return getPersonDisplayName(appeal.created_by as any)
}

const cardTitle = computed(() => {
  if (isCreateMode.value) return 'Создание нового обращения'
  if (!currentAppeal.value?.number && !currentAppeal.value?.id) return 'Загрузка...'
  return `Обращение №${currentAppeal.value?.number ?? currentAppeal.value?.id?.slice(0, 8) ?? ''}`
})

const actionButtonText = computed(() => (isEditMode.value ? 'Сохранить' : 'Создать'))

/*
 * Онбординг: форма — контекст шагов по полям и «ушкам», панель задач — вложенный контекст.
 * showDemo/showReal подменяют реальное обращение учебным, когда обучение запущено из открытой формы.
 */
useOnboardingContext({
  names: [APPEALS_CONTEXTS.form, APPEALS_CONTEXTS.formTasks],
  scenarioId: 'appeals.form',
  root: () => document.body,
  actions: {
    showDemo: () => {
      demoOverrideId.value = onboardingStore.demo.appeal?.[APPEALS_FORM_DEMO_INDEX]?.detail.id != null
        ? String(onboardingStore.demo.appeal[APPEALS_FORM_DEMO_INDEX].detail.id)
        : null
      applyDemoAppeal()
    },
    showReal: async () => {
      demoOverrideId.value = null
      if (entityId.value && !isDemo.value)
        await loadData(entityId.value)
    },
    openTasksPane: () => { isTasksPaneVisible.value = true },
    closeTasksPane: () => {
      // В embedded-режиме панель задач открыта по умолчанию — закрываем только на узких экранах
      if (!formEmbeddedLayout.isEmbedded.value)
        isTasksPaneVisible.value = false
    },
  },
})
</script>

<template>
  <!-- Blur-подложка для embedded-режима (по эталону TicketForm.vue / FormAdverseEvent.vue) -->
  <Teleport to="body">
    <div
      v-if="formEmbeddedLayout.isEmbedded.value && visible"
      class="entity-form-backdrop"
    />
  </Teleport>

  <VDialog
    v-model="visible"
    :z-index="2400"
    :max-width="formEmbeddedLayout.maxWidth.value"
    :content-class="overlayContentClass"
    :scrim="!formEmbeddedLayout.isEmbedded.value"
    persistent
    @click:outside="onDialogDismissAttempt"
    @keydown.esc.stop="onDialogDismissAttempt"
  >
    <!-- Кнопки в правом верхнем углу (Teleport — ушко как в НС и Заявках) -->
    <Teleport to="body">
      <div
        v-if="formEmbeddedLayout.isEmbedded.value && visible"
        class="entity-form-actions"
      >
        <EntityPaneEar
          v-if="entityId && hasTasksPane"
          kind="tasks"
          :active="isRightPaneVisible"
          @click="toggleTasksPane"
        />

        <ReportTemplateMenu v-if="entityId" object-type="appeal" :object-id="entityId" variant="ear" />
        <CopyEntityLink
          v-if="entityId"
          variant="ear"
          :to="{
            name: ERouteNames.CITIZEN_INQUIRY_DETAIL,
            params: { id: entityId },
          }"
        />
        <OnboardingLaunchButton v-if="entityId" variant="ear" />

        <VBtn
          icon="ri-close-line"
          color="primary"
          class="entity-form-action-btn"
          aria-label="Закрыть"
          @click="handleClose"
        />
      </div>
    </Teleport>

    <VCard :class="{ 'entity-form-card h-100': formEmbeddedLayout.isEmbedded.value || pinCreateChrome }">
      <div
        :class="(formEmbeddedLayout.isEmbedded.value || pinCreateChrome)
          ? 'entity-form-body d-flex h-100 overflow-hidden'
          : 'entity-form-mobile-pane-body'"
      >
        <!-- ========================================== -->
        <!-- ЛЕВАЯ ПАНЕЛЬ: ФОРМА ОБРАЩЕНИЯ И ОТВЕТ      -->
        <!-- ========================================== -->
        <!-- Панель кнопок разделов на мобильных устройствах НАД названием; закреплена при прокрутке -->
        <div
          v-if="isEditMode && !formEmbeddedLayout.isEmbedded.value && xs"
          class="entity-form-mobile-actions d-flex align-center justify-space-evenly w-100"
        >
          <VTooltip
            v-if="hasTasksPane"
            location="bottom"
            text="Задачи"
          >
            <template #activator="{ props: tipProps }">
              <VBtn
                v-bind="tipProps"
                icon="ri-list-check"
                variant="text"
                :color="isRightPaneVisible ? 'primary' : 'secondary'"
                aria-label="Задачи"
                @click="toggleTasksPane"
              />
            </template>
          </VTooltip>

          <ReportTemplateMenu v-if="entityId" object-type="appeal" :object-id="entityId" variant="inline" />
          <CopyEntityLink
            v-if="entityId"
            variant="inline"
            :to="{ name: ERouteNames.CITIZEN_INQUIRY_DETAIL, params: { id: entityId } }"
          />
          <OnboardingLaunchButton v-if="entityId" variant="inline" />

          <CustomCloseButton :close-action="handleClose" />
        </div>
        <div
          :class="(formEmbeddedLayout.isEmbedded.value || pinCreateChrome)
            ? 'entity-form-pane flex-grow-1 position-relative'
            : ((!formEmbeddedLayout.isEmbedded.value && isRightPaneVisible) ? 'position-relative d-flex flex-column' : 'position-relative d-flex flex-column flex-grow-1')"
        >
          <VCardTitle
            class="d-flex pa-6"
            :class="{
              'flex-column pb-2': isEditMode && !formEmbeddedLayout.isEmbedded.value && xs,
              'px-4 py-3': xs,
            }"
          >

            <div class="form_card d-flex justify-space-between align-center w-100">
              <div class="form_card__title">
                <span class="text-h4" :class="{ 'font-weight-semibold': isEditMode }">
                  {{ cardTitle }}
                </span>
              </div>

              <!-- В обычном режиме на десктопе ИЛИ в режиме создания на всех экранах кнопки справа в строке -->
              <div
                v-if="!formEmbeddedLayout.isEmbedded.value && (!xs || isCreateMode)"
                class="d-flex align-center gap-2"
              >
                <template v-if="isEditMode">
                  <VTooltip
                    v-if="hasTasksPane"
                    location="bottom"
                    text="Задачи"
                  >
                    <template #activator="{ props: tipProps }">
                      <VBtn
                        v-bind="tipProps"
                        icon="ri-list-check"
                        variant="text"
                        color="primary"
                        aria-label="Задачи"
                        @click="toggleTasksPane"
                      />
                    </template>
                  </VTooltip>
                  <ReportTemplateMenu v-if="entityId" object-type="appeal" :object-id="entityId" variant="inline" />
                  <CopyEntityLink
                    v-if="entityId"
                    variant="inline"
                    :to="{ name: ERouteNames.CITIZEN_INQUIRY_DETAIL, params: { id: entityId } }"
                  />
                  <OnboardingLaunchButton v-if="entityId" variant="inline" />
                </template>
                <CustomCloseButton :close-action="handleClose" />
              </div>
            </div>
          </VCardTitle>

          <VForm :class="(!formEmbeddedLayout.isEmbedded.value && !pinCreateChrome) ? ((!formEmbeddedLayout.isEmbedded.value && isRightPaneVisible) ? 'd-none' : 'd-flex flex-column flex-grow-1') : undefined" @submit.prevent="onSaveAppeal">
            <!-- Вкладки и чипы статуса в режиме редактирования (по эталону TicketForm.vue) -->
            <VTabs v-if="isEditMode && currentAppeal" :model-value="0">
              <div class="d-flex gap-2">
                <VTab :value="0">
                  Форма
                </VTab>
              </div>

              <div class="d-flex gap-2 align-center flex-wrap">
                <VChip
                  v-if="currentAppeal.channel?.name"
                  size="small"
                  variant="tonal"
                  color="secondary"
                >
                  {{ currentAppeal.channel.name }}
                </VChip>

                <VChip
                  v-if="currentAppeal.status"
                  :class="currentAppeal.status"
                  class="status-badge"
                  size="small"
                >
                  {{ APPEAL_STATUS_LABELS[currentAppeal.status] || currentAppeal.status }}
                </VChip>

                <VChip
                  v-if="currentAppeal.topic"
                  size="small"
                  variant="outlined"
                >
                  {{ APPEAL_TOPIC_LABELS[currentAppeal.topic] || currentAppeal.topic }}
                </VChip>

                <VChip
                  v-if="currentAppeal.rating"
                  size="small"
                  variant="outlined"
                  prepend-icon="ri-star-fill"
                  class="text-warning"
                >
                  {{ currentAppeal.rating }} / 5
                </VChip>
              </div>
            </VTabs>

            <div class="entity-form-scroll-body" :class="{ 'mt-4': isEditMode }">
              <VCol v-if="isCreateMode || currentAppeal" class="d-flex flex-column gap-4 px-0">
                <!-- Канал поступления, Тема обращения и Оценка (SearchableSelectList по эталону проекта) -->
                <div
                  class="d-flex gap-2"
                  :class="{ 'flex-column': !mdAndUp }"
                  :data-onboarding="APPEALS_ANCHORS.form.channelTopic"
                >
                  <!-- Канал поступления -->
                  <div :class="!mdAndUp ? 'w-100' : 'w-50'">
                    <VMenu
                      v-model="showChannelMenu"
                      :disabled="!canEditField('channel')"
                      eager
                      :close-on-content-click="false"
                      :max-height="350"
                      content-class="entity-form-menu"
                    >
                      <template #activator="{ props: menuProps }">
                        <VTextField
                          :model-value="selectedChannelLabel"
                          class="invisible-readonly"
                          :class="{ 'cursor-pointer': canEditField('channel') }"
                          :append-inner-icon="canEditField('channel') ? (showChannelMenu ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line') : undefined"
                          variant="outlined"
                          density="comfortable"
                          placeholder="Выберите канал поступления"
                          persistent-placeholder
                          readonly
                          :disabled="!canEditField('channel')"
                          :error="validationErrors.channel"
                          v-bind="canEditField('channel') ? menuProps : {}"
                          @click:append-inner.stop="canEditField('channel') && (showChannelMenu = !showChannelMenu)"
                        >
                          <template #label>
                            <span>Канал поступления<sup v-if="canEditField('channel')" class="required-label">*</sup></span>
                          </template>
                        </VTextField>
                      </template>

                      <SearchableSelectList
                        :items="channelSelectItems"
                        :loading="store.isChannelsLoading"
                        placeholder="Поиск канала..."
                        @select="onSelectChannel"
                      />
                    </VMenu>
                  </div>

                  <!-- Тема обращения -->
                  <div :class="!mdAndUp ? 'w-100' : 'w-50'">
                    <VMenu
                      v-model="showTopicMenu"
                      :disabled="!canEditField('topic')"
                      eager
                      :close-on-content-click="false"
                      :max-height="300"
                      content-class="entity-form-menu"
                    >
                      <template #activator="{ props: menuProps }">
                        <VTextField
                          :model-value="selectedTopicLabel"
                          class="invisible-readonly"
                          :class="{ 'cursor-pointer': canEditField('topic') }"
                          :append-inner-icon="canEditField('topic') ? (showTopicMenu ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line') : undefined"
                          variant="outlined"
                          density="comfortable"
                          placeholder="Выберите тему обращения"
                          persistent-placeholder
                          readonly
                          :disabled="!canEditField('topic')"
                          :error="validationErrors.topic"
                          v-bind="canEditField('topic') ? menuProps : {}"
                          @click:append-inner.stop="canEditField('topic') && (showTopicMenu = !showTopicMenu)"
                        >
                          <template #label>
                            <span>Тема обращения<sup v-if="canEditField('topic')" class="required-label">*</sup></span>
                          </template>
                        </VTextField>
                      </template>

                      <SearchableSelectList
                        :items="topicSelectItems"
                        placeholder="Поиск темы..."
                        @select="onSelectTopic"
                      />
                    </VMenu>
                  </div>
                </div>

                <!-- Оценка заявителя (только для темы «Отзыв») -->
                <div v-if="form.topic === 'review'" :data-onboarding="APPEALS_ANCHORS.form.rating">
                  <VMenu
                    v-model="showRatingMenu"
                    :disabled="!canEditField('rating')"
                    eager
                    :close-on-content-click="false"
                    :max-height="300"
                    content-class="entity-form-menu"
                  >
                    <template #activator="{ props: menuProps }">
                      <VTextField
                        :model-value="selectedRatingLabel"
                        class="invisible-readonly"
                        :class="{ 'cursor-pointer': canEditField('rating') }"
                        :append-inner-icon="canEditField('rating') ? (showRatingMenu ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line') : undefined"
                        variant="outlined"
                        density="comfortable"
                        placeholder="Выберите оценку от 1 до 5"
                        persistent-placeholder
                        readonly
                        :disabled="!canEditField('rating')"
                        :error="validationErrors.rating"
                        v-bind="canEditField('rating') ? menuProps : {}"
                        @click:append-inner.stop="canEditField('rating') && (showRatingMenu = !showRatingMenu)"
                      >
                        <template #label>
                          <span>Оценка заявителя<sup v-if="canEditField('rating')" class="required-label">*</sup></span>
                        </template>
                      </VTextField>
                    </template>

                    <SearchableSelectList
                      :items="ratingSelectItems"
                      placeholder="Выберите оценку..."
                      @select="onSelectRating"
                    />
                  </VMenu>
                </div>

                <!-- ФИО заявителя -->
                <div v-if="canEditField('name') || form.name" :data-onboarding="APPEALS_ANCHORS.form.applicant">
                  <VTextField
                    v-model="form.name"
                    variant="outlined"
                    density="comfortable"
                    placeholder="Введите ФИО заявителя"
                    persistent-placeholder
                    :readonly="!canEditField('name')"
                    :error="validationErrors.name"
                    @update:model-value="validationErrors.name = false"
                  >
                    <template #label>
                      <span>ФИО заявителя<sup v-if="canEditField('name')" class="required-label">*</sup></span>
                    </template>
                  </VTextField>
                </div>

                <!-- Контактный телефон и Email -->
                <div
                  v-if="canEditField('phone') || canEditField('email') || form.phone || form.email"
                  class="d-flex align-start gap-2"
                  :class="{ 'flex-column': !mdAndUp }"
                  :data-onboarding="APPEALS_ANCHORS.form.contacts"
                >
                  <VMaskInput
                    v-if="canEditField('phone') || form.phone"
                    v-model="form.phone"
                    mask="+7 (###) ###-##-##"
                    hide-details
                    :class="!mdAndUp ? 'w-100' : 'w-50'"
                    variant="outlined"
                    density="comfortable"
                    placeholder="+7 (___) ___-__-__"
                    persistent-placeholder
                    :readonly="!canEditField('phone')"
                  >
                    <template #label>
                      <span>Контактный телефон</span>
                    </template>
                  </VMaskInput>

                  <VTextField
                    v-if="canEditField('email') || form.email"
                    v-model="form.email"
                    :class="!mdAndUp ? 'w-100' : 'w-50'"
                    variant="outlined"
                    density="comfortable"
                    type="email"
                    placeholder="Для отправки официального ответа"
                    persistent-placeholder
                    :readonly="!canEditField('email')"
                  >
                    <template #label>
                      <span>Email</span>
                    </template>
                  </VTextField>
                </div>

                <!-- Тип адресата и выбор Отделения (NewTreeComponent) или Сотрудника (EmployeeSelectionModal) -->
                <div
                  v-if="canEditField('target_type')"
                  class="d-flex gap-2"
                  :class="{ 'flex-column': !mdAndUp }"
                  :data-onboarding="APPEALS_ANCHORS.form.target"
                >
                  <div :class="form.target_type ? (!mdAndUp ? 'w-100' : 'w-50') : 'w-100'">
                    <VMenu
                      v-model="showTargetTypeMenu"
                      eager
                      :close-on-content-click="false"
                      :max-height="260"
                      content-class="entity-form-menu"
                    >
                      <template #activator="{ props: menuProps }">
                        <VTextField
                          :model-value="selectedTargetTypeLabel"
                          class="invisible-readonly cursor-pointer"
                          :append-inner-icon="showTargetTypeMenu ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"
                          variant="outlined"
                          density="comfortable"
                          placeholder="Выберите тип адресата (необязательно)"
                          persistent-placeholder
                          readonly
                          clearable
                          v-bind="menuProps"
                          @click:append-inner.stop="showTargetTypeMenu = !showTargetTypeMenu"
                          @click:clear="onClearTargetType"
                        >
                          <template #label>
                            <span>Кому адресовано</span>
                          </template>
                        </VTextField>
                      </template>

                      <SearchableSelectList
                        :items="targetTypeSelectItems"
                        placeholder="Выберите тип..."
                        @select="onSelectTargetType"
                      />
                    </VMenu>
                  </div>

                  <!-- Если выбран тип «Отделение» — дерево подразделений -->
                  <NewTreeComponent
                    v-if="form.target_type === 'department'"
                    :class="!mdAndUp ? 'w-100' : 'w-50'"
                    placeholder="Выберите отделение"
                    label="Отделение"
                    search-placeholder="Поиск по отделениям"
                    :selected="{ value: selectedTargetDepartment }"
                    :tree-data="apiStore.departmentsTree"
                    :search-tree="searchDepartments"
                    @update:selected="onTargetDepartmentSelect"
                  />

                  <!-- Если выбран тип «Сотрудник» — выбор из модалки сотрудников -->
                  <VTextField
                    v-else-if="form.target_type === 'employee'"
                    :model-value="form.target_name"
                    :class="!mdAndUp ? 'w-100' : 'w-50'"
                    class="invisible-readonly cursor-pointer"
                    variant="outlined"
                    density="comfortable"
                    placeholder="Выберите сотрудника"
                    persistent-placeholder
                    readonly
                    clearable
                    @click="openTargetEmployeeModal"
                    @click:clear="form.target_name = ''"
                  >
                    <template #label>
                      <span>Сотрудник</span>
                    </template>
                  </VTextField>
                </div>

                <!-- Просмотр адресата в readonly-режиме -->
                <div v-else-if="currentAppeal?.target_name" :data-onboarding="APPEALS_ANCHORS.form.target">
                  <VTextField
                    :model-value="currentAppeal.target_name"
                    variant="outlined"
                    density="comfortable"
                    readonly
                  >
                    <template #label>
                      <span>{{ currentAppeal.target_type === 'employee' ? 'Сотрудник' : 'Отделение' }}</span>
                    </template>
                  </VTextField>
                </div>

                <!-- Содержание обращения -->
                <div :data-onboarding="APPEALS_ANCHORS.form.text">
                  <VTextarea
                    v-model="form.text"
                    variant="outlined"
                    density="comfortable"
                    placeholder="Опишите суть обращения гражданина"
                    persistent-placeholder
                    :readonly="!canEditField('text')"
                    :error="validationErrors.text"
                    rows="3"
                    @update:model-value="validationErrors.text = false"
                  >
                    <template #label>
                      <span>Содержание обращения<sup v-if="canEditField('text')" class="required-label">*</sup></span>
                    </template>
                  </VTextarea>
                </div>

                <!-- Список прикрепленных файлов через штатный FileList -->
                <FileList
                  v-if="existingFiles.length"
                  :items="existingFiles"
                  :show-delete="false"
                />

                <!-- ============================================================ -->
                <!-- БЛОК ОТВЕТА ЗАЯВИТЕЛЮ ПО ЭЛЕКТРОННОЙ ПОЧТЕ (по аналогии с ППУ) -->
                <!-- ============================================================ -->
                <!-- Блок виден, только если ответ уже отправлен или пользователь может ответить (координатор) -->
                <template v-if="isEditMode && currentAppeal && (currentAppeal.status === 'answered' || currentAppeal.reply_text || canReplyAppeal)">
                  <CustomDivider title="Ответ заявителю по электронной почте" />

                  <!-- 1. Если ответ уже отправлен -->
                  <div
                    v-if="currentAppeal.status === 'answered' || currentAppeal.reply_text"
                    class="d-flex flex-column gap-4"
                    :data-onboarding="APPEALS_ANCHORS.form.reply"
                  >
                    <VTextarea
                      :model-value="currentAppeal.reply_text || ''"
                      density="comfortable"
                      variant="outlined"
                      readonly
                      rows="3"
                    >
                      <template #label>
                        <span>Текст отправленного ответа</span>
                      </template>
                    </VTextarea>
                  </div>

                  <!-- 2. Если обращение новое и пользователь — назначенный координатор (или суперадмин) -->
                  <div
                    v-else-if="canReplyAppeal"
                    class="d-flex flex-column gap-4"
                    :data-onboarding="APPEALS_ANCHORS.form.reply"
                  >
                    <VAlert
                      v-if="!currentAppeal.email"
                      type="warning"
                      variant="tonal"
                      density="comfortable"
                    >
                      Email заявителя не был указан при регистрации обращения. Укажите адрес электронной почты, на который следует отправить официальный ответ.
                    </VAlert>

                    <VTextField
                      v-if="!currentAppeal.email"
                      v-model="replyEmail"
                      density="comfortable"
                      variant="outlined"
                      type="email"
                      placeholder="Введите email получателя ответа..."
                      persistent-placeholder
                      :disabled="feedbackStore.isGlobalLoading"
                    >
                      <template #label>
                        <span>Email для отправки ответа<sup class="required-label">*</sup></span>
                      </template>
                    </VTextField>

                    <VTextField
                      v-model="replySubject"
                      density="comfortable"
                      variant="outlined"
                      placeholder="Тема ответа"
                      persistent-placeholder
                      :disabled="feedbackStore.isGlobalLoading"
                    >
                      <template #label>
                        <span>Тема ответа<sup class="required-label">*</sup></span>
                      </template>
                    </VTextField>

                    <VTextarea
                      v-model="replyBody"
                      density="comfortable"
                      variant="outlined"
                      placeholder="Введите текст официального ответа гражданину..."
                      persistent-placeholder
                      rows="3"
                      :disabled="feedbackStore.isGlobalLoading"
                    >
                      <template #label>
                        <span>Текст ответа заявителю<sup class="required-label">*</sup></span>
                      </template>
                    </VTextarea>

                    <div class="d-flex justify-end">
                      <VBtn
                        :block="xs"
                        variant="outlined"
                        color="primary"
                        prepend-icon="ri-send-plane-line"
                        :loading="store.isReplying"
                        :disabled="!effectiveAppealEmail || !replySubject.trim() || !replyBody.trim() || feedbackStore.isGlobalLoading"
                        @click="onSendReply"
                      >
                        Отправить ответ
                      </VBtn>
                    </div>
                  </div>

                </template>
              </VCol>
            </div>

            <!-- Футер левой панели (структура один-в-один из TicketForm.vue) -->
            <footer class="d-flex flex-column gap-2 body-2">
              <CustomDivider v-if="isEditMode && currentAppeal" />

              <div
                class="d-flex gap-4"
                :class="xs ? 'flex-column' : 'align-start justify-space-between'"
              >
                <div
                  v-if="isEditMode && currentAppeal"
                  class="d-flex flex-column gap-2"
                  :class="{ 'order-2': xs }"
                >
                  <div v-if="getApplicantName(currentAppeal)" class="d-flex gap-2 align-center">
                    <VIcon icon="ri-user-3-line" size="16" />
                    <span>Заявитель: {{ getApplicantName(currentAppeal) }}</span>
                  </div>

                  <div v-if="getCreatedByLabel(currentAppeal)" class="d-flex gap-2 align-center">
                    <VIcon icon="ri-user-add-line" size="16" />
                    <span>Создатель: {{ getCreatedByLabel(currentAppeal) }}</span>
                  </div>

                  <div v-if="currentAppeal.created_at" class="d-flex gap-2 align-center">
                    <VIcon icon="ri-time-line" size="16" />
                    <span>Дата и время регистрации: {{ formatDateTime(currentAppeal.created_at) }}</span>
                  </div>

                  <div v-if="currentAppeal.replied_at" class="d-flex gap-2 align-center text-success">
                    <VIcon icon="ri-checkbox-circle-line" size="16" color="success" />
                    <span>
                      Дата и время ответа: {{ formatDateTime(currentAppeal.replied_at) }}
                      {{ getRepliedByLabel(currentAppeal) ? `(${getRepliedByLabel(currentAppeal)})` : '' }}
                    </span>
                  </div>
                </div>
                <div v-else-if="!xs" class="flex-grow-1" />

                <div
                  class="d-flex gap-2"
                  :class="xs ? 'order-1 flex-column' : 'flex-shrink-0 align-center'"
                >
                  <VBtn
                    v-if="canAssignMyself"
                    prepend-icon="ri-user-shared-line"
                    variant="outlined"
                    :block="xs"
                    @click="assignMyselfAsCoordinator"
                  >
                    Назначить себя
                  </VBtn>

                  <VBtn
                    v-if="isCreateMode || (hasEditableFields && isFormChanged)"
                    :block="xs"
                    :loading="store.isSaving"
                    @click="onSaveAppeal"
                  >
                    {{ actionButtonText }}
                  </VBtn>
                </div>
              </div>
            </footer>
          </VForm>
        </div>

        <!-- Разделитель между формой и правой панелью -->
        <div
          v-if="formEmbeddedLayout.isEmbedded.value && isRightPaneVisible"
          class="entity-form-resizer"
          :class="{ 'is-resizing': chatPaneResizer.isResizing.value }"
          @mousedown="chatPaneResizer.startResize"
        />

        <!-- ========================================== -->
        <!-- ПРАВАЯ ПАНЕЛЬ: ЗАДАЧИ И КООРДИНАТОР         -->
        <!-- ========================================== -->
        <div
          v-if="isRightPaneVisible"
          :class="formEmbeddedLayout.isEmbedded.value
            ? 'entity-form-chat-pane overflow-hidden pa-0 d-flex flex-column'
            : 'd-flex flex-column flex-grow-1 min-h-0 min-w-0 h-100'"
          :style="formEmbeddedLayout.isEmbedded.value ? { flexBasis: `${chatPaneResizer.width.value}px` } : undefined"
        >

          <TaskCard
            class="flex-grow-1"
            v-if="coordinatorTask"
            :task="coordinatorTask"
            :is-demo="isDemo"
            @updated="handleCoordinatorTaskUpdated"
          />
          <TaskUnassignedCard
            v-else
            :data-onboarding="APPEALS_ANCHORS.form.assignCoordinator"
            title="Задача координатора"
            :performer-label="'Исполнитель -\nкоординатор'"
            hint="Координатор ещё не назначен"
            :can-assign="canPickAnyCoordinator || canAssignMyself"
            :assign-label="canPickAnyCoordinator ? 'Назначить' : 'Назначить себя'"
            @assign="canPickAnyCoordinator ? openCoordinatorModal() : assignMyselfAsCoordinator()"
          />
        </div>
      </div>
    </VCard>
  </VDialog>

  <!-- Модальное окно выбора координатора -->
  <EmployeeSelectionModal
    ref="coordinatorModalRef"
    :employees="apiStore.employees"
    :multiple="false"
    title="Выберите координатора"
    apply-label="Назначить"
    @select="onCoordinatorSelected"
  />

  <!-- Модальное окно выбора сотрудника-адресата -->
  <EmployeeSelectionModal
    ref="targetEmployeeModalRef"
    :employees="apiStore.employees"
    :multiple="false"
    title="Выберите сотрудника"
    @select="onTargetEmployeeSelected"
  />

  <!-- Диалог подтверждения несохраненных изменений -->
  <ConfirmDialog
    :is-dialog-visible="showUnsavedChangesDialog"
    confirmation-question="Закрыть без сохранения?"
    confirmation-text="Процесс будет прерван, данные не сохранятся"
    confirm-btn="Закрыть"
    reject-btn="Отменить"
    @confirm="handleCloseConfirm"
    @update:is-dialog-visible="showUnsavedChangesDialog = $event"
  />
</template>

<style scoped lang="scss">
:deep(.v-slide-group__content) {
  justify-content: space-between;
}

:deep(.status-badge.answered) {
  background-color: rgb(var(--v-theme-success), var(--v-activated-opacity)) !important;
  color: rgb(var(--v-theme-success)) !important;
}

:deep(.status-badge.new) {
  background-color: rgb(var(--v-theme-info), var(--v-activated-opacity)) !important;
  color: rgb(var(--v-theme-info)) !important;
}
</style>
